import { useCallback, useEffect, useRef, useState } from 'react';

export default function useOptimizerPortfolioSummaries({
    userUid,
    posSizingVersion,
    watchlistVersion = 0,
    refreshEnabled = true,
    fetchOptimizerPortfolios,
    exclusiveCoreLoadOwnerRef = null,
}) {
    const scopeKey = `${userUid || ''}:${watchlistVersion}`;
    const currentScopeRef = useRef(scopeKey);
    currentScopeRef.current = scopeKey;
    const requestSequenceRef = useRef(0);
    const [refreshState, setRefreshState] = useState(null);
    const preparedScopeRef = useRef(null);
    const fetchOptimizerPortfolioSummaries = useCallback(async ({
        isCurrentRequest = null,
        force = false,
        stagePublicationState = null,
        preparedTarget = null,
    } = {}) => {
        const staging = typeof stagePublicationState === 'function';
        const isCurrent = () => (
            (!exclusiveCoreLoadOwnerRef?.current || (staging && typeof isCurrentRequest === 'function'))
            && (typeof isCurrentRequest !== 'function' || isCurrentRequest())
        );
        if (!userUid) {
            return { status: 'skipped', reason: 'no_user' };
        }
        if (!isCurrent()) {
            return { status: 'superseded' };
        }
        const sequence = ++requestSequenceRef.current;
        const targetScopeKey = preparedTarget
            ? `${userUid}:${preparedTarget.versions.watchlistVersion}` : scopeKey;
        const targetPosSizingVersion = preparedTarget?.versions.posSizingVersion ?? posSizingVersion;
        const ownsStatus = () => currentScopeRef.current === scopeKey
            && requestSequenceRef.current === sequence && isCurrent();
        const setCurrentStatus = (status) => {
            if (!ownsStatus()) return;
            const apply = () => {
                setRefreshState({ scopeKey: targetScopeKey, status });
                if (staging && status === 'complete') {
                    preparedScopeRef.current = { scopeKey: targetScopeKey, posSizingVersion: targetPosSizingVersion };
                }
            };
            if (staging) stagePublicationState(apply, ownsStatus);
            else apply();
        };
        if (!staging) setCurrentStatus('loading');
        try {
            const resp = await fetchOptimizerPortfolios({
                force,
                isCurrentRequest: isCurrent,
                ...(staging ? { stagePublicationState, preparedTarget } : {}),
            });
            if (!isCurrent()) {
                setCurrentStatus('superseded');
                return { status: 'superseded' };
            }
            setCurrentStatus(['error', 'superseded'].includes(resp?.status) ? resp.status : 'complete');
            return resp;
        } catch (err) {
            if (!isCurrent()) {
                setCurrentStatus('superseded');
                return { status: 'superseded' };
            }
            setCurrentStatus('error');
            console.error('Error fetching optimizer portfolios:', err);
            return {
                status: 'error',
                code: err?.code || 'optimizer_summaries_refresh_failed',
                error: err,
            };
        }
    }, [fetchOptimizerPortfolios, scopeKey, userUid, posSizingVersion, exclusiveCoreLoadOwnerRef]);

    useEffect(() => {
        if (!refreshEnabled || (posSizingVersion <= 0 && watchlistVersion <= 0)) {
            return;
        }
        if (preparedScopeRef.current?.scopeKey === scopeKey
            && preparedScopeRef.current.posSizingVersion === posSizingVersion) return;
        void fetchOptimizerPortfolioSummaries({ force: true });
    }, [posSizingVersion, watchlistVersion, refreshEnabled, fetchOptimizerPortfolioSummaries, scopeKey]);

    return {
        fetchOptimizerPortfolioSummaries,
        refreshStatus: !refreshEnabled ? 'deferred'
            : refreshState?.scopeKey === scopeKey ? refreshState.status : 'pending',
    };
}
